 import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';

import { BgBomCategory, BgBomGroupedSubCategory, CombinedBgBomItem, BgBomItem,BgBomSubCategory } from '../models/bg.model';
import { ProjectModel } from '../models/projectModel';
import { AuthService } from './auth.service';
import { normalizeFileName } from '../utils/stringUtils';

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  private groupColor = '002980BA';
  private pageHeaderColor = '000C54A0';
  private footerLinkColor = '000C54A0';
  private tableHeaderBgColor = '000C54A0';
  private tableHeaderTextColor = '00FFFFFF';
  private tableAlternateColor = '00DDDDDD';

  private totalHeaderColor = '0066B0FF';
  private totalHeaderTextColor = '00000000';
  private totalEvenColor = '00E6F2FF';
  private totalOddColor = '00CCE5FF';

  private projectNumberFontSize = 12;
  private projectNameFontSize = 16;
  private bomFontSize = 16;
  private categoryFontSize = 16;
  private clientFontSize = 16;
  private footerFontSize = 10;

  constructor(
    private authService: AuthService
  ) { }

  private columns = [
    {
      name: '#',
      width: 5,
    },
    {
      name: 'Item',
      width: 25,
    },
    {
      name: 'Size',
      width: 20,
    },
    {
      name: 'Description',
      width: 30,
    },
    {
      name: 'Manufacturer',
      width: 30,
    },
    {
      name: 'Model',
      width: 15,
    },
    {
      name: 'Quantity',
      width: 20,
      align: 'center',
    },
    {
      name: 'Waste Factor',
      width: 15,
      align: 'center',
    },
    {
      name: 'Quantity to Order',
      width: 20,
      align: 'center',
    },
    {
      name: 'Unit Price',
      width: 15,
      align: 'center',
    },
    {
      name: 'Total Price',
      width: 15,
      align: 'center',
    },

  ];

  exportModelToExcel(
    modelName: string,
    categories: Array<BgBomCategory>,
    projectModel: ProjectModel,
    client?: string
  ): void {
    const loggedInClient = this.authService.getClientDataStructure();

    const workbook = new ExcelJS.Workbook();

    const buildExcelAndDownload = async (imageLogoId?: number) => {
      if (loggedInClient?.configuration?.excelSingleSpreadsheet) {
        // single spreadsheet
        let worksheet = workbook.addWorksheet("All Categories");
        this.addHeader(worksheet, client, imageLogoId, projectModel);

        categories?.forEach((category) => {
          this.addCategoryHeader(worksheet, category.name.toUpperCase());
          this.addTable(worksheet, category);
          worksheet.addRow([]);
        });
        this.addFooter(worksheet);
      }
      else {
        // spreadsheet by category
        categories?.forEach((category) => {
          let worksheet = workbook.addWorksheet(category.name);
          this.addHeader(worksheet, client, imageLogoId, projectModel);
          this.addCategoryHeader(worksheet, category.name.toUpperCase());
          this.addTable(worksheet, category);
          this.addFooter(worksheet);
        });
      }


      let title = modelName?.replace(/\s/, '')?.replace(/[^A-Z0-9]/gi, '_');
      workbook.title = title ?? '';
      const bytes = await workbook.xlsx.writeBuffer();
      const data = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      this.downloadFile(data, normalizeFileName(`${projectModel?.projectName}.xlsx`));
    };

    // if customLogo explicitly set to empty string in the DB - don't show logo at all
    const showLogo = loggedInClient?.configuration?.customLogo !== "";
    if (showLogo) {
     const logoSrc = loggedInClient?.configuration?.customLogo ? loggedInClient?.configuration?.customLogo : 'assets/img/pdf-logo.png';
    //  const logoSrc =  'assets/img/pdf-logo.png';

      fetch(logoSrc).then(async (r) => {
        const imageContents = await r.arrayBuffer();
        const imageLogoId = workbook.addImage({
          buffer: imageContents,
          extension: 'png',
        });

        await buildExcelAndDownload(imageLogoId);
      });
    } else {
      buildExcelAndDownload().catch(console.error);
    }
  }

  private addBomItems(
    subCategory: BgBomSubCategory,
    dataRowNumber: number,
    category: BgBomCategory,
    worksheet: ExcelJS.Worksheet,
    isTotal: boolean
  ) {
    let lastItemName: string | undefined = '';
    let lastItemNameAndSize: string | undefined = '';
    subCategory.bgBomItems.forEach((item) => {
      let currentItemNameAndSize = `${item.itemName}${item.size}`;
      worksheet.addRow([
        dataRowNumber++,
        lastItemName === item.itemName && category.name.toLowerCase() === 'framing' ? '' : item.itemName ?? '',
        item.size ?? '',
        item.description ?? '',
        item.manufacturer ?? '',
        item.manufacturerModel ?? '',
        `${item.modelQuantity ?? ''} ${item.modelQuantityDescription}`,
        item.wasteFactor ?? '',
        `${item.quantityToOrder ?? ''} ${item.quantityToOrderDescription}`,
        item.cost ? this.formatCurrency(item.cost) : 'N/A',
        item.totalCost ? this.formatCurrency(item.totalCost) : 'N/A',
      ]);
      lastItemName = item.itemName;
      lastItemNameAndSize = currentItemNameAndSize;

      let bomItemRow = worksheet.getRow(worksheet.rowCount);
      this.columns.forEach((c) => {
        let cell = bomItemRow.getCell(this.columns.indexOf(c) + 1);
        cell.alignment = { horizontal: <any>c.align };

        let rowFillColor = this.getRowFillColor(worksheet.rowCount, isTotal);
        if (rowFillColor) {
          cell.fill = rowFillColor;
        }
      });
    });
    return dataRowNumber;
  }


    formatCurrency = (value: number | undefined) =>
     value !== undefined  && value !== 0  ? `$${value.toFixed(2)}` : 'N/A';

  private addSubcategoryRow(
    subCategory: BgBomSubCategory,
    worksheet: ExcelJS.Worksheet,
    isTotal: boolean
  ) {
    worksheet.addRow([subCategory.name]);
    let currentRowIndex = worksheet.rowCount;

    worksheet.mergeCells(
      currentRowIndex,
      1,
      currentRowIndex,
      worksheet.columnCount
    );

    let subCategoryCell = worksheet.getCell(currentRowIndex, 1);

    let rowFillColor = this.getRowFillColor(worksheet.rowCount, isTotal, true);
    if (rowFillColor) {
      subCategoryCell.fill = rowFillColor;
    }

    if (isTotal) {
      subCategoryCell.font = {
        bold: true,
        color: { argb: this.totalHeaderTextColor },
      };
    } else {
      subCategoryCell.font = {
        bold: true,
        color: { argb: this.groupColor },
      };
    }
    return isTotal;
  }

  private addGroupRow(
    group: BgBomGroupedSubCategory,
    worksheet: ExcelJS.Worksheet
  ) {
    if (group.name?.toUpperCase() !== 'DEFAULT') {
      worksheet.addRow([group.name]);

      worksheet.mergeCells(
        worksheet.rowCount,
        1,
        worksheet.rowCount,
        worksheet.columnCount
      );

      let groupCell = worksheet.getCell(worksheet.rowCount, 1);
      groupCell.style = { alignment: { horizontal: 'center' } };
      groupCell.font = { bold: true, color: { argb: this.groupColor } };
      let rowFillColor = this.getRowFillColor(worksheet.rowCount);
      if (rowFillColor) {
        groupCell.fill = rowFillColor;
      }
    }
  }



  private addTableColumns(worksheet: ExcelJS.Worksheet) {
    worksheet.addRow(this.columns.map((i) => i.name));
    this.columns.forEach((c, i) => {
      worksheet.getColumn(i + 1).width = c.width;

      let headerCell = worksheet.getCell(worksheet.rowCount, i + 1);
      headerCell.style = {
        fill: {
          pattern: 'solid',
          type: 'pattern',
          fgColor: { argb: this.tableHeaderBgColor },
        },
      };
      headerCell.font = {
        bold: true,
        color: { argb: this.tableHeaderTextColor },
      };
    });
  }

  private getRowFillColor(
    index: number,
    isTotal: boolean = false,
    isSubCategoryHeader: boolean = false
  ): ExcelJS.Fill | null {
    if (isTotal) {
      if (isSubCategoryHeader) {
        return {
          pattern: 'solid',
          type: 'pattern',
          fgColor: { argb: this.totalHeaderColor },
        };
      }

      return {
        pattern: 'solid',
        type: 'pattern',
        fgColor: {
          argb: index % 2 == 0 ? this.totalEvenColor : this.totalOddColor,
        },
      };
    }

    if (index % 2 === 0) {
      return {
        fgColor: { argb: this.tableAlternateColor },
        type: 'pattern',
        pattern: 'solid',
      };
    }

    return null;
  }

  private addHeader(
    worksheet: ExcelJS.Worksheet,
    client: string | undefined,
    imageLogoId?: number,
    projectModel?: ProjectModel
  ) {
    worksheet.mergeCells(1, 1, 1, 2);
    let clientCell = worksheet.getCell(1, 1);
    clientCell.value = client?.toUpperCase();
    clientCell.font = {
      size: this.clientFontSize,
      bold: true,
      color: { argb: this.pageHeaderColor },
    };

    worksheet.mergeCells(1, 3, 1, 7);
    let bomTitleCell = worksheet.getCell(1, 3);
    bomTitleCell.value = 'BILL OF MATERIALS';
    bomTitleCell.font = {
      size: this.bomFontSize,
      bold: true,
      color: { argb: this.pageHeaderColor },
    };
    bomTitleCell.alignment = { horizontal: 'center' };

    const projectNameRowIndex = 3;

    const projectNameCell = worksheet.getCell(projectNameRowIndex, 1);
    projectNameCell.value = projectModel?.projectName ?? ''; // name of type
    projectNameCell.font = {
      size: this.projectNameFontSize,
      bold: true,
      color: { argb: this.pageHeaderColor },
    };

    const projectNumberCell = worksheet.getCell(projectNameRowIndex + 1, 1);
    projectNumberCell.value = projectModel?.projectNumber ?? '';
    projectNumberCell.font = {
      size: this.projectNumberFontSize,
      bold: false,
      color: { argb: this.pageHeaderColor },
    };

    if (imageLogoId !== undefined) {
      let scale = 0.35;
      worksheet.addImage(imageLogoId, {
        tl: { col: 7, row: 0 },
        ext: { width: scale * 634, height: scale * 82 },
      });
    }
  }

  private addCategoryHeader(
    worksheet: ExcelJS.Worksheet,
    categoryName: string,
  ) {
    worksheet.addRow([]);
    worksheet.mergeCells(worksheet.rowCount, 3, worksheet.rowCount, 7);
    let categoryTitleCell = worksheet.getCell(worksheet.rowCount, 3);
    categoryTitleCell.value = categoryName;
    categoryTitleCell.font = {
      size: this.categoryFontSize,
      bold: true,
      color: { argb: this.pageHeaderColor },
    };
    categoryTitleCell.alignment = { horizontal: 'center' };
  }

  private addFooter(worksheet: ExcelJS.Worksheet) {
    let today = new Date();
    let todayString = today.toLocaleDateString();
    worksheet.addRow([]);
    worksheet.addRow([]);

    worksheet.mergeCells(
      worksheet.rowCount,
      1,
      worksheet.rowCount,
      this.columns.length - 2
    );

    let noteCell = worksheet.getCell(worksheet.rowCount, 1);
    noteCell.value =
      'NOTE: Quantities and waste factors to be verified by Suppliers and Trade Partners';
    noteCell.style.font = {
      size: this.footerFontSize,
    };
    worksheet.mergeCells(
      worksheet.rowCount,
      this.columns.length - 1,
      worksheet.rowCount,
      this.columns.length
    );
    let todayCell = worksheet.getCell(
      worksheet.rowCount,
      this.columns.length - 1
    );
    todayCell.value = todayString;
    todayCell.style.font = {
      size: this.footerFontSize,
    };
    todayCell.alignment = { horizontal: 'right' };

    worksheet.addRow([]);

    const loggedInClient = this.authService.getClientDataStructure();

    // if customWebSite explicitly set to empty string in the DB - don't show website at all
    const showWebSite = loggedInClient?.configuration?.customWebSite !== "";
    if (showWebSite) {
      worksheet.mergeCells(
        worksheet.rowCount,
        1,
        worksheet.rowCount,
        this.columns.length - 2
      );

      const urlSrc = loggedInClient?.configuration?.customWebSite ? loggedInClient?.configuration?.customWebSite : 'www.builtgenix.com';

      let companyUrlCell = worksheet.getCell(worksheet.rowCount, 1);
      companyUrlCell.value = urlSrc;
      companyUrlCell.style.font = {
        size: this.footerFontSize,
        color: { argb: this.footerLinkColor },
      };
    }

    // if customEmail explicitly set to empty string in the DB - don't show email at all
    const showEmail = loggedInClient?.configuration?.customEmail !== "";
    if (showEmail) {
      worksheet.mergeCells(
        worksheet.rowCount,
        this.columns.length - 1,
        worksheet.rowCount,
        this.columns.length
      );
      const contactsCell = worksheet.getCell(
        worksheet.rowCount,
        this.columns.length - 1
      );
      const emailSrc = loggedInClient?.configuration?.customEmail ? loggedInClient?.configuration?.customEmail : 'contacts@builtgenix.com';
      contactsCell.value = emailSrc;
      contactsCell.alignment = { horizontal: 'right' };
      contactsCell.style.font = {
        size: this.footerFontSize,
        color: { argb: this.footerLinkColor },
      };
    }
  }

  private downloadFile(data: Blob, fileName: string) {
    const anchorElement = document.createElement('a');

    document.body.appendChild(anchorElement);

    anchorElement.style.display = 'none';

    const url = window.URL.createObjectURL(data);

    anchorElement.href = url;
    anchorElement.download = fileName;
    anchorElement.click();

    window.URL.revokeObjectURL(url);
  }

  exportDynamicModelToExcel(
    modelName: string,
    categories: any[], // Array<BgBomCategory>,
    projectModel: ProjectModel,
    client?: string
  ): void {
    const loggedInClient = this.authService.getClientDataStructure();

    const workbook = new ExcelJS.Workbook();

    const buildExcelAndDownload = async (imageLogoId?: number) => {

      // Step 1: Collect Dynamic Columns
      const quantityKeysSet = new Set<string>();
      const keyMapping: { [simplifiedKey: string]: string } = {};

      (categories as BgBomCategory[])?.forEach((BgBomCategory) => {
         BgBomCategory?.bgGroupedSubCategories?.forEach((groupSubCategory) => {
          groupSubCategory?.bgSubCategories?.forEach((subCategory) => {
            subCategory.combinedBgBomItems?.forEach((item: CombinedBgBomItem) => {
              if (item.combinedQuantityToOrder) {
                Object.keys(item.combinedQuantityToOrder).forEach((originalKey: string) => {
                  const parts = originalKey.split('_');
                  const simplifiedKey = parts.slice(-2).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
                  quantityKeysSet.add(simplifiedKey);
                   keyMapping[simplifiedKey] = originalKey;
                });
              }
            });
          });
        });
      });



      const combinedQuantityKeys = Array.from(quantityKeysSet);

      // Define displayed columns including dynamic columns
      const displayedColumns = [

        'itemName',
        'size',
        'description',
        'modelQuantity',
        'modelQuantityDescription',
      ].concat(combinedQuantityKeys);


      const dynamicColumns: ColumnDefinition[] = displayedColumns.map(colKey => {
        return this.getColumnDefinitionForKey(colKey);
      });

        // single spreadsheet
        let worksheet = workbook.addWorksheet("All Categories");
        this.addHeader(worksheet, client, imageLogoId, projectModel);

        categories?.forEach((category) => {
          this.addCategoryHeader(worksheet, category.name.toUpperCase());
          // Step 2: Pass dynamic columns to addTable method
          this.addDynamicTable(worksheet, category, dynamicColumns, combinedQuantityKeys, keyMapping);
          worksheet.addRow([]);
        });
        this.addFooter(worksheet);

      let title = modelName?.replace(/\s/, '')?.replace(/[^A-Z0-9]/gi, '_');
      workbook.title = title ?? '';
      const bytes = await workbook.xlsx.writeBuffer();
      const data = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      this.downloadFile(data, normalizeFileName(`${projectModel?.projectName}.xlsx`));
    };

    // if customLogo explicitly set to empty string in the DB - don't show logo at all
    const showLogo = loggedInClient?.configuration?.customLogo !== "";
    if (showLogo) {
     const logoSrc = loggedInClient?.configuration?.customLogo ? loggedInClient?.configuration?.customLogo : 'assets/img/pdf-logo.png';
      // const logoSrc =  'assets/img/pdf-logo.png';

      fetch(logoSrc).then(async (r) => {
        const imageContents = await r.arrayBuffer();
        const imageLogoId = workbook.addImage({
          buffer: imageContents,
          extension: 'png',
        });

        await buildExcelAndDownload(imageLogoId);
      });
    } else {
      buildExcelAndDownload().catch(console.error);
    }
  }



  private addTable(worksheet: ExcelJS.Worksheet, category: BgBomCategory) {
    let dataRowNumber = 1;
    worksheet.addRow([]); // add empty row
    this.addTableColumns(worksheet);

    category.bgGroupedSubCategories?.forEach((group) => {
      this.addGroupRow(group, worksheet);

      group.bgSubCategories?.forEach((subCategory) => {
        const isTotal = subCategory.name.toLowerCase().startsWith('total');
        this.addSubcategoryRow(subCategory, worksheet, isTotal);

        dataRowNumber = this.addBomItems(
          subCategory,
          dataRowNumber,
          category,
          worksheet,
          isTotal
        );
      });
    });
  }


  addDynamicTable(
    worksheet: ExcelJS.Worksheet,
    category: BgBomCategory,
    dynamicColumns: ColumnDefinition[],
    combinedQuantityKeys: string[],
    keyMapping: { [simplifiedKey: string]: string }
  ) {
    // Write the header row
    const headerRow = dynamicColumns.map(colDef => colDef.name);
    const headerRowRef = worksheet.addRow(headerRow);

    // Set column widths and styles
    dynamicColumns.forEach((colDef, index) => {
      const columnIndex = index + 1;
      const headerCell = headerRowRef.getCell(columnIndex);

      headerCell.style = {
        fill: {
          pattern: 'solid',
          type: 'pattern',
          fgColor: { argb: this.tableHeaderBgColor },
        },
        font: {
          bold: true,
          color: { argb: this.tableHeaderTextColor },
        },
      };

      worksheet.getColumn(columnIndex).width = colDef.width;
      if (colDef.align) {
        worksheet.getColumn(columnIndex).alignment = { horizontal: colDef.align as any };
      }
    });

    // Write the data rows
    category?.bgGroupedSubCategories?.forEach(groupSubCategory => {
      this.addGroupRow(groupSubCategory, worksheet);
      groupSubCategory?.bgSubCategories?.forEach(subCategory => {
        const isTotal = subCategory.name.toLowerCase().startsWith('total');
        this.addSubcategoryRow(subCategory, worksheet, isTotal);
        subCategory?.combinedBgBomItems?.forEach(item => {
          const dataRow = dynamicColumns.map(colDef => this.getItemData(item, colDef.key, combinedQuantityKeys, keyMapping));
          const rowRef = worksheet.addRow(dataRow);

          dynamicColumns.forEach((colDef, index) => {
            let cell = rowRef.getCell(index + 1);
            if (colDef.align) {
              cell.alignment = { horizontal: colDef.align as any };
            }

            let rowFillColor = this.getRowFillColor(worksheet.rowCount, isTotal);
            if (rowFillColor) {
              cell.fill = rowFillColor;
            }
          });
        });
      });
    });
  }



  getColumnHeader(columnKey: string): string {
    const headersMap: { [key: string]: string } = {
      itemName: 'Item Name',
      size: 'Size',
      description: 'Description',
      modelQuantity: 'Model Quantity',
      modelQuantityDescription: 'Units of Measurement',
      // For dynamic columns, use the simplifiedKey as is
    };

    return headersMap[columnKey] || columnKey; // For dynamic columns, the key itself can be used as header
  }




  getItemData(
    item: BgBomItem,
    columnKey: string,
    combinedQuantityKeys: string[],
    keyMapping: { [simplifiedKey: string]: string }
  ): any {
    if (columnKey === 'itemName') return item.itemName;
    if (columnKey === 'size') return item.size;
    if (columnKey === 'description') return item.description;
    if (columnKey === 'modelQuantity') return item.modelQuantity;
    if (columnKey === 'modelQuantityDescription') return item.modelQuantityDescription;

    if (combinedQuantityKeys.includes(columnKey)) {
      const combinedItem = item as CombinedBgBomItem;
      if (combinedItem.combinedQuantityToOrder) {
        const originalKey = keyMapping[columnKey];
        const value = combinedItem.combinedQuantityToOrder[originalKey];
        return value;
      }
    }

    return '';
  }
   getColumnDefinitionForKey(colKey: string): ColumnDefinition {
    const columnKeyToNameMap: { [key: string]: string } = {
      itemName: 'Item',
      size: 'Size',
      description: 'Description',
      modelQuantity: 'Quantity',
      modelQuantityDescription: 'Units of Measurement',
      // Add more mappings as needed
    };

    const headerName = columnKeyToNameMap[colKey] || colKey;

    const columnDefinition = this.columns.find(c => c.name === headerName);

    const width = columnDefinition?.width || 15;
    const align = columnDefinition?.align;

    return {
      key: colKey,
      name: headerName,
      width: width,
      align: align,
    };
  }







}
interface ColumnDefinition {
  key: string;  // Column key, e.g., 'itemName'
  name: string; // Header name, e.g., 'Item Name'
  width: number;
  align?: string;
}
